import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Sparkles, Target, Clock, Shield, Star } from 'lucide-react';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="bg-primary-500 p-2 rounded-lg">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">First Ping</h1>
                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 md:mt-1">
                  <span className="font-extrabold text-primary-600">AI</span> Powered Outreach
                </p>
              </div>
            </div>
            <div className="space-x-4">
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Generate High-Converting
            <span className="text-primary-600 block">Cold Emails</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Stop struggling with generic templates. First Ping uses AI to create personalized cold emails that get responses, with LinkedIn integration and real-time company news.
          </p>
          <Link
            to="/signup"
            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-medium text-lg flex items-center space-x-2 mx-auto w-fit transition-colors"
          >
            <Sparkles className="h-5 w-5" />
            <span>Start Generating Emails</span>
          </Link>
          <p className="text-gray-500 mt-4">5 free credits • No credit card required</p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-primary-100 p-3 rounded-full w-fit mx-auto mb-4">
              <Target className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Smart Targeting</h3>
            <p className="text-gray-600">
              Target businesses or individuals with LinkedIn profile data for hyper-personalized outreach.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-primary-100 p-3 rounded-full w-fit mx-auto mb-4">
              <Sparkles className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Content</h3>
            <p className="text-gray-600">
              Get 3 unique email variations with compelling subject lines powered by Google Gemini AI.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="bg-primary-100 p-3 rounded-full w-fit mx-auto mb-4">
              <Clock className="h-6 w-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time News</h3>
            <p className="text-gray-600">
              Incorporate recent company news and events for timely, relevant cold outreach.
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Perfect for Freelancers & Agencies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Star className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">24+ Services Covered</h4>
                  <p className="text-gray-600">Web development, design, marketing, SEO, and more</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Star className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">8 Different Tones</h4>
                  <p className="text-gray-600">Professional, friendly, casual, confident, and more</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Star className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">20+ Business Types</h4>
                  <p className="text-gray-600">SaaS, e-commerce, coaching, local businesses, and more</p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">LinkedIn Integration</h4>
                  <p className="text-gray-600">Extract profile data for hyper-personalized emails</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">Mobile Responsive</h4>
                  <p className="text-gray-600">Generate emails on any device, anywhere</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-primary-600 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900">One-Click Copy</h4>
                  <p className="text-gray-600">Easy clipboard integration for quick workflow</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Cold Outreach?
          </h3>
          <p className="text-xl text-gray-600 mb-8">
            Join freelancers and agencies who are already generating high-converting cold emails.
          </p>
          <Link
            to="/signup"
            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-medium text-lg flex items-center space-x-2 mx-auto w-fit transition-colors"
          >
            <Mail className="h-5 w-5" />
            <span>Get Started Free</span>
          </Link>
          <p className="text-gray-500 mt-4">No credit card required • 5 free credits to start</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-gray-600">
            Built for freelancers and agencies to create high-converting cold emails
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;